import type { ServicePageData } from './types';

const vehicleSignwriting: ServicePageData = {
  slug: 'vehicle-signwriting',
  eyebrow: 'Vehicle graphics',
  eyebrowTo: '/services/vehicle-signwriting',
  title: 'Let your vehicle',
  titleAccent: 'do the talking.',
  intro:
    'Turn up to your next job looking professional, on-brand and, most importantly, get noticed by potential customers. Using premium materials and specialist finishes, we will transform your vehicle.',
  heroPrimaryButtonText: 'Explore Options',
  heroSecondaryButtonText: 'Get in Touch',
  hidePerfectFor: true,
  hideCustomisation: true,
  heroImages: [
    '/home/vehicles/outpost-van.jpg',
    '/home/vehicles/green-man.jpg',
    '/vehicle-signwriting/vehicle1.jpg',
    '/home/vehicles/kdyt-car.jpg',
    '/home/vehicles/west-midlands-heating.jpg',
    '/home/vehicles/magnetic-bridgwaters.jpg',
    '/vehicle-signwriting/vehicle2.jpg',
    '/home/vehicles/kdyt-minibus.jpg',
    '/home/vehicles/dkr-renovations.jpg',
    '/home/vehicles/shire-structures.jpg',
    '/vehicle-signwriting/vehicle3.jpg',
    '/home/vehicles/paisley-flour.jpg',
    '/home/vehicles/oasis-blinds.jpg',
    '/home/vehicles/magnetic-paisley.jpg',
    '/vehicle-signwriting/vehicle4.jpg',
    '/home/vehicles/thomas-weston.jpg',
    '/home/vehicles/wfk.jpg',
    '/home/vehicles/wilmer-bts.jpg',
    '/vehicle-signwriting/vehicle5.jpg',
    '/home/vehicles/magnetic-bewdley-candle.jpg',
    '/home/vehicles/magnetic-hero.jpg',
  ],

  useCases: [
    { title: "Cars and 'Caddy' size vans", text: 'Cut or printed vinyl to all panels. Logo, contact details and a strapline.' },
    { title: "SWB to LWB 'Transit' size vans", text: 'The workhorse. Side panels, rear doors and bonnet, cut or printed.' },
    { title: 'Luton and box vans', text: 'Big flat panels that take full-colour print like a billboard.' },
    { title: 'Fleet livery', text: 'Consistent branding across every vehicle, from a library of scaled drawings.' },
    { title: 'Minibuses and community transport', text: 'Sponsor logos and route branding for charities and clubs.' },
    { title: 'Business and personal use', text: 'Magnetic signs you can take off for the weekend or a client visit.' },
  ],

  optionsHeading: 'What do we offer',
  options: [
    {
      id: 'commercial-signwriting',
      title: 'Commercial Signwriting',
      lead: 'The classic signwritten look. Sharp edges, no background.',
      body: 'A machine follows your design with a blade, cutting letters and shapes from pre-coloured rolls. Colours come as separate pieces, layered by hand, so once applied it can look hand painted. The colour runs all the way through the vinyl, so it resists sun and fade better than print.',
      image: '/home/vehicles/green-man.jpg',
      specs: [
        { label: 'Best for', value: 'Logos, lettering and contact details in solid colours.' },
        { label: 'Longevity', value: 'More resistant to sunlight than print. Ideal for vehicles and windows.' },
        { label: 'Removal', value: 'Thinner than print. Tricky to start an edge, then peels rather than snaps.' },
      ],
    },
    {
      id: 'fleet-branding',
      title: 'Fleet Branding',
      lead: 'Consistent branding across every vehicle in your fleet.',
      body: 'From single vans to entire commercial fleets, we maintain complete brand consistency across every make and model in your fleet, working from scaled manufacturer drawings.',
      image: '/home/vehicles/dkr-renovations.jpg',
      specs: [
        { label: 'Coverage', value: 'Cars, caddy vans, panel vans, tippers and box trucks.' },
        { label: 'Consistency', value: 'Exact Pantone-matched colours and scaled layouts across all vehicles.' },
        { label: 'Fitting', value: 'On-site fleet fitting or at our Kidderminster workshop.' },
      ],
    },
    {
      id: 'removable-solutions',
      title: 'Removable Solutions',
      lead: 'Promote your business and services, some of the time.',
      body: 'Use your vehicle for business and personal? Consult for more than one company? Graphics applied to magnetic sheeting come off when you need them to. Two standard sizes, or custom shapes cut to your graphic or panel. We colour-match the sheet to your paintwork so it blends in.',
      image: '/home/vehicles/magnetic-bewdley-candle.jpg',
      buttons: [
        { label: 'Find out more…', to: '/services/magnetic-panels' },
      ],
      specs: [
        { label: 'Sizes', value: '60 × 50 cm and 60 × 100 cm as standard. Custom sizes and shapes available.' },
        { label: 'Fit', value: 'Flat panels and gently curved doors. Not suited to dips, ribs or bumps.' },
        { label: 'Life', value: 'About two years with proper care, then replace before it lets go at speed.' },
      ],
    },
    {
      id: 'decorative-graphics',
      title: 'Decorative Graphics',
      lead: 'Full colour, photos, gradients and shading.',
      body: 'Printed on our in-house wide-format printer onto white or clear vinyl, then laminated. Matches Pantone, RGB or CMYK references and applies like a large peel-and-stick sticker, so it is quicker to install than cut vinyl.',
      image: '/home/vehicles/kdyt-minibus.jpg',
      specs: [
        { label: 'Best for', value: 'Complex artwork, photographic images and multi-colour designs.' },
        { label: 'Colour', value: 'Pantone, RGB or CMYK matched. Laminated for protection.' },
        { label: 'Removal', value: 'Thicker and laminated, so it gets brittle after years in the sun. We offer a removal service.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Free mockup on your vehicle',
      text: 'Standard vehicles come from our library of scaled drawings. Off-spec vehicles get photographed and measured at our unit, or send your own photos and measurements.',
    },
  ],

  gallery: [],

  steps: [
    {
      title: 'Arrange a free consultation',
      text: 'Standard vehicle? We use our library of scaled drawings. A little off-spec? Pop down to our Kidderminster unit and we will take photos and measurements, or send your own.',
    },
    {
      title: 'Free quote and mockup',
      text: 'Send us your logo and our design team will put together a visual of how your vehicle could look. Need a logo designed first? We can help with that.',
    },
    {
      title: 'Approve and book a slot',
      text: 'Sign off the mockup and quote, then book with our production team. Late-night slots on Wednesdays, midday slots on Saturdays, or we come to you on site.',
    },
    {
      title: 'Order up',
      text: 'Bring your vehicle to our unit, or our fully insured installation team meets you at a location of your choice. We do our job while you do yours.',
    },
  ],

  info: [
    {
      title: 'Installation',
      text: 'Our professional install team has years of experience applying flawless decals, available throughout the Midlands.',
      bullets: ['Fully insured', 'On-site or at our unit', 'Late nights Wednesday, midday Saturday'],
    },
    {
      title: 'It pays for itself',
      text: 'Compare the cost of £500 of signwriting to any other advertising. A vehicle is seen by thousands of road users every day, 365 days a year, even when you are off the clock.',
    },
    {
      title: 'Vinyl removal',
      text: 'Selling the vehicle or rebranding? We remove old graphics too, including brittle printed vinyl that snaps when you try it yourself.',
    },
  ],

  faqs: [
    {
      q: 'Can I put a magnetic sign on a curved door?',
      a: 'Most likely. Magnetic signs stay in place on doors and side panels with a slight curve, and work very well on flat surfaces. Surfaces with dips or bumps are not recommended.',
    },
    {
      q: 'How long will a magnetic sign last?',
      a: 'With proper care and use, about two years. At that point we strongly recommend replacing it to prevent it falling off while you drive.',
    },
    {
      q: 'Can I put a sign on a new paint job?',
      a: 'No. Wait for the paint to cure, and the same applies to a new clear coat or wax. Recommended wait times are 90 days for paint, 60 days for clear coat and 2 days for wax.',
    },
    {
      q: 'Will I ever have to take the magnets off?',
      a: 'Yes. Magnets are for short-term use. Take them off at least once a week and wash and dry the paint underneath. For permanent branding, choose cut or printed vinyl.',
    },
    {
      q: 'Can I wash the vehicle with the signs on?',
      a: 'Remove them before washing.',
    },
    {
      q: 'Which lasts longer, cut or printed vinyl?',
      a: 'Cut vinyl. The colour runs through the material rather than sitting on the surface, so it fades less in sunlight. Printed vinyl is laminated to protect it but will still age faster.',
    },
  ],

  darkFaq: true,
  greenCta: true,

  cta: {
    title: 'Ready to get noticed on the road?',
    text: 'Free consultation, free mockup and a quote for cut vinyl, printed graphics or magnetic signs.',
    image: '/home/vehicles/kdyt-car.jpg',
  },
};

export default vehicleSignwriting;
